import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight, Camera, Loader2, Package, Share2, Store, Upload, Globe,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import ConnectPrimaryCard from "@/components/ConnectPrimaryCard";
import { toast } from "sonner";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, saveOnboarding } = useAuth();
  const { countries, currencies, setCountry, setCurrency } = useLocale();
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["seller-onboarding"],
    queryFn: async () => (await api.get("/seller/onboarding")).data,
    refetchOnMount: "always",
  });
  const { data: connStatus } = useQuery({
    queryKey: ["seller-connections"],
    queryFn: async () => (await api.get("/seller/connections")).data,
  });
  const [picked, setPicked] = useState("");

  useEffect(() => {
    if (data?.onboarding) {
      setForm(data.onboarding);
      const main = data.onboarding.main_marketplace;
      if (main && main !== "shoptosa") setPicked(main);
    }
  }, [data]);

  const steps = data?.steps || [];
  const stepIndex = Math.max(0, steps.findIndex((s) => s.id === form?.step));
  const current = steps[stepIndex] || steps[0];
  const firstName = (user?.name || "there").split(" ")[0];

  const connectedMap = useMemo(() => {
    const out = {};
    for (const ch of connStatus?.channels || []) {
      if (ch.connected) out[ch.id] = true;
    }
    return out;
  }, [connStatus]);

  const patch = async (body, { next } = {}) => {
    setBusy(true);
    try {
      const saved = await saveOnboarding(body);
      setForm(saved.onboarding);
      if (next) navigate(next);
      return saved;
    } catch (e) {
      toast.error(apiError(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const continueTo = async (step, extra = {}) => {
    return patch({ ...form, ...extra, step });
  };

  const finish = async (next = "/seller") => {
    const saved = await patch({ ...form, done: true, step: "ready" }, { next });
    if (saved) toast.success("Shop setup complete");
  };

  const skip = async () => {
    await finish("/seller");
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/seller/crosslist/import.csv", fd);
      toast.success(`Imported ${res.imported} listings as drafts`);
      await continueTo("ready", { imported: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setImporting(false);
    }
  };

  if (isLoading || !form) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-aura-muted">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className={`${form.step === "marketplaces" ? "max-w-3xl" : "max-w-xl"} mx-auto px-6 pt-28 pb-24`} data-testid="seller-onboarding">
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Seller setup</div>
      <div className="flex items-center justify-between mb-8">
        <ol className="flex gap-1.5" data-testid="onboarding-progress">
          {steps.map((s, i) => (
            <li
              key={s.id}
              className={`h-1.5 w-8 rounded-full ${i <= stepIndex ? "bg-aura-accent" : "bg-aura-border"}`}
            />
          ))}
        </ol>
        <button type="button" onClick={skip} className="text-xs text-aura-muted hover:text-aura-ink" data-testid="onboarding-skip">
          Skip for now
        </button>
      </div>

      {form.step === "welcome" && (
        <div>
          <h1 className="font-serif text-4xl tracking-tight mb-4">Welcome, {firstName}</h1>
          <p className="text-aura-muted leading-relaxed mb-8">
            We’ll name your shop, pick where you already sell, then import inventory.
            Connect eBay, Etsy, Depop, Amazon, Shopify, or Flipkart from Connections. For Poshmark and similar sites, click Connect on the channel page, then list with the helper.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => continueTo("shop")}
            data-testid="onboarding-continue"
            className="w-full py-3.5 btn-accent rounded-full text-sm font-semibold flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get started <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      )}

      {form.step === "shop" && (
        <div>
          <h1 className="font-serif text-4xl tracking-tight mb-2">Your shop</h1>
          <p className="text-sm text-aura-muted mb-8">{current?.blurb}</p>
          <div className="space-y-4 mb-8">
            <label className="block text-xs text-aura-muted mb-1">Boutique name</label>
            <input
              value={form.shop_name}
              onChange={(e) => setForm((f) => ({ ...f, shop_name: e.target.value }))}
              data-testid="onboarding-shop-name"
              className="w-full px-4 py-3 bg-aura-surface border border-aura-border focus:border-aura-accent outline-none text-sm rounded-lg"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-aura-muted mb-1">Origin country</label>
                <select
                  value={form.origin_country}
                  onChange={(e) => {
                    const code = e.target.value;
                    setForm((f) => ({ ...f, origin_country: code }));
                    setCountry(code);
                  }}
                  data-testid="onboarding-country"
                  className="w-full px-4 py-3 bg-aura-surface border border-aura-border text-sm rounded-lg"
                >
                  {(countries || []).map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-aura-muted mb-1">Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => {
                    const code = e.target.value;
                    setForm((f) => ({ ...f, currency: code }));
                    setCurrency(code);
                  }}
                  data-testid="onboarding-currency"
                  className="w-full px-4 py-3 bg-aura-surface border border-aura-border text-sm rounded-lg"
                >
                  {(currencies || []).map((c) => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.ships_worldwide}
                onChange={(e) => setForm((f) => ({ ...f, ships_worldwide: e.target.checked }))}
              />
              Ships worldwide
            </label>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => continueTo("welcome")} className="px-5 py-3 border border-aura-border rounded-full text-sm">Back</button>
            <button
              type="button"
              disabled={busy || !form.shop_name.trim()}
              onClick={() => continueTo("marketplaces")}
              data-testid="onboarding-continue"
              className="flex-1 py-3 btn-accent rounded-full text-sm font-semibold"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {form.step === "marketplaces" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <button type="button" onClick={() => continueTo("shop")} className="text-xs text-aura-muted hover:text-aura-ink">Back</button>
          </div>
          <ConnectPrimaryCard
            selected={picked}
            onSelect={setPicked}
            connected={connectedMap}
            busy={busy}
            onConfirm={async () => {
              if (!picked) return;
              const extras = {
                main_marketplace: picked,
                channels: ["shoptosa", picked, ...(form.channels || []).filter((c) => c !== picked && c !== "shoptosa")],
              };
              await continueTo("import_inventory", extras);
            }}
            onCreateItem={async () => {
              const saved = await continueTo("import_inventory", { main_marketplace: "shoptosa" });
              if (saved) navigate("/seller/add");
            }}
          />
        </div>
      )}

      {form.step === "import_inventory" && (
        <div>
          <h1 className="font-serif text-4xl tracking-tight mb-2">Import inventory</h1>
          <p className="text-sm text-aura-muted mb-8">
            {data?.product_count
              ? `You already have ${data.product_count} listing${data.product_count === 1 ? "" : "s"} in this shop.`
              : "Bring pieces in from photos or a CSV export. Drafts stay off the storefront until you publish."}
          </p>
          <div className="space-y-3 mb-8">
            <button
              type="button"
              onClick={async () => {
                const saved = await patch({ ...form, step: "import_inventory" });
                if (saved) navigate("/seller/add");
              }}
              className="w-full flex items-start gap-4 p-5 rounded-lg border border-aura-border hover:border-aura-accent transition-colors text-left"
              data-testid="onboarding-closet"
            >
              <Package className="w-5 h-5 text-aura-accent mt-0.5" />
              <div>
                <div className="font-medium">Closet ingest</div>
                <div className="text-sm text-aura-muted mt-1">Drop photos. AI writes titles, brands, and prices as drafts.</div>
              </div>
            </button>
            <label className="flex items-start gap-4 p-5 rounded-lg border border-aura-border hover:border-aura-accent transition-colors cursor-pointer">
              {importing ? <Loader2 className="w-5 h-5 animate-spin mt-0.5" /> : <Upload className="w-5 h-5 text-aura-cyan mt-0.5" />}
              <div>
                <div className="font-medium">CSV from another marketplace</div>
                <div className="text-sm text-aura-muted mt-1">Title column required. Imported as drafts.</div>
              </div>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} />
            </label>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => continueTo("marketplaces")} className="px-5 py-3 border border-aura-border rounded-full text-sm">Back</button>
            <button
              type="button"
              disabled={busy}
              onClick={() => continueTo("ready")}
              data-testid="onboarding-continue"
              className="flex-1 py-3 btn-accent rounded-full text-sm font-semibold"
            >
              I’ll add pieces later
            </button>
          </div>
        </div>
      )}

      {form.step === "ready" && (
        <div>
          <h1 className="font-serif text-4xl tracking-tight mb-2">You’re ready to list.</h1>
          <p className="text-sm text-aura-muted mb-8">
            Next: studio shots, publish on Shoptosa, then connect other shops from Connections.
          </p>
          <div className="space-y-3 mb-8">
            <button type="button" onClick={() => finish("/seller/photoshoot")} className="w-full flex items-start gap-4 p-5 rounded-lg border border-aura-border hover:border-aura-accent text-left">
              <Camera className="w-5 h-5 text-aura-accent mt-0.5" />
              <div>
                <div className="font-medium">AI studio</div>
                <div className="text-sm text-aura-muted mt-1">Turn a flat lay into a model shot.</div>
              </div>
            </button>
            <button type="button" onClick={() => finish("/seller/products")} className="w-full flex items-start gap-4 p-5 rounded-lg border border-aura-border hover:border-aura-accent text-left">
              <Store className="w-5 h-5 text-aura-cyan mt-0.5" />
              <div>
                <div className="font-medium">Publish to shop</div>
                <div className="text-sm text-aura-muted mt-1">Go live on shoptosa.com when a draft is ready.</div>
              </div>
            </button>
            <button type="button" onClick={() => finish("/seller/crosslist")} className="w-full flex items-start gap-4 p-5 rounded-lg border border-aura-border hover:border-aura-accent text-left">
              <Share2 className="w-5 h-5 text-aura-accent mt-0.5" />
              <div>
                <div className="font-medium">Crosslist hub</div>
                <div className="text-sm text-aura-muted mt-1">CSV and dashboard Post for eBay, Etsy, Depop, Amazon, Shopify, and Flipkart.</div>
              </div>
            </button>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => finish("/seller")}
            data-testid="onboarding-finish"
            className="w-full py-3.5 btn-accent rounded-full text-sm font-semibold flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Go to dashboard <Globe className="w-4 h-4" /></>}
          </button>
        </div>
      )}
    </div>
  );
}
